/**
 * Base64 payload prefixes and the MIME type they identify.
 * The payload bytes are the real format, so they are checked before `imageFormat`.
 *
 * @type {Array<[string, string]>}
 */
const BASE64_SIGNATURES = [
  ['iVBORw0KGgo', 'image/png'],
  ['/9j/', 'image/jpeg'],
  ['UklGR', 'image/webp'],
  ['R0lGOD', 'image/gif'],
  ['PHN2Zy', 'image/svg+xml'],
  ['PD94', 'image/svg+xml'],
];

/**
 * `imageFormat` values whose MIME type is not `image/<value>`.
 *
 * @type {Object<string, string>}
 */
const IMAGE_FORMAT_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
};

/**
 * Turn a catalog product `image` into a value ready for `<img src>`
 * The ancillaries service may send a URL, a data URI or a bare base64 payload
 *
 * @param {string|null|undefined} image - Product image as received from the ancillaries service
 * @param {string} [imageFormat] - Product image format, used as MIME fallback when the
 *   payload signature is unknown
 * @returns {string} URL or data URI, or `''` when the image is missing or unidentifiable,
 *   so components fall back to the snack placeholder
 *
 * @example
 * toProductImageSrc('https://example.com/cocacola.jpg', 'jpg');
 * // Returns: 'https://example.com/cocacola.jpg'
 *
 * toProductImageSrc('iVBORw0KGgo...', 'jpg');
 * // Returns: 'data:image/png;base64,iVBORw0KGgo...' — the signature wins over imageFormat
 *
 * toProductImageSrc('QUJD...');
 * // Returns: '' — unknown signature and no imageFormat
 */
export default function toProductImageSrc(image, imageFormat) {
  if (!image) return '';

  if (/^(data:|https?:\/\/)/.test(image)) return image;

  const signature = BASE64_SIGNATURES.find(([prefix]) => image.startsWith(prefix));
  const mime = signature
    ? signature[1]
    : imageFormat && (IMAGE_FORMAT_MIME[imageFormat] || `image/${imageFormat}`);

  return mime ? `data:${mime};base64,${image}` : '';
}
